import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSupport } from '../../redux/ducks/support';
import { isStringNotEmpty } from '../../utils/objectUtils';
import { EMAIL_REGEX } from '../../utils/validationUtils';
import { AgreementModalButton, AgreementsModalButton } from '../../pages/Agreements/Agreement';

/**
 * GuestRegistrationForm - Inline form for unauthenticated users
 *
 * Collects name, email, and phone number before starting a support chat.
 * Creates an account on the spot via the createGuestSupportAccount Cloud Function.
 */
const GuestRegistrationForm = () => {

	//#region useSupport

	const { registerGuestAndStartChat, isLoading, error } = useSupport();

	//#endregion
	//#region useStates

	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [isAccepted, setIsAccepted] = useState(false);
	const [validationError, setValidationError] = useState(null);

	//#endregion
	//#region handleSubmit

	/** Simple email format validation using regex */
	const validateEmail = (email) => {
		return EMAIL_REGEX.test(email);
	};

	/**
	 * Validates form fields and calls registerGuestAndStartChat.
	 * If the email already has an account, shows a "please log in" message.
	 */
	const handleSubmit = async (e) => {
		e.preventDefault();
		setValidationError(null);

		if (!isStringNotEmpty(name?.trim())) {
			setValidationError('Please enter your name.');
			return;
		}

		if (!isStringNotEmpty(email?.trim()) || !validateEmail(email.trim())) {
			setValidationError('Please enter a valid email address.');
			return;
		}

		if (!isAccepted) {
			setValidationError('Agreements must be accepted.');
			return;
		}

		const result = await registerGuestAndStartChat(name.trim(), email.trim(), phone.trim() || null);

		if (result?.exists) {
			setValidationError('An account with this email already exists. Please log in to use support chat.');
		}
	};

	//#endregion
	//#region returnJSX

	return (
		<div className="support-guest-form">
			{/* Header with welcome text */}
			<div className="support-guest-form-header">
				<h6 className="mb-1">Welcome to ReadyRx Support</h6>
				<p className="text-muted small mb-3">
					Please provide your contact info to start chatting with our support team.
				</p>
			</div>

			<Form onSubmit={handleSubmit}>
				{/* Name field (required) */}
				<Form.Group className="mb-3">
					<Form.Label className="small fw-semibold">Name *</Form.Label>
					<Form.Control
						type="text"
						placeholder="Your full name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						disabled={isLoading}
						size="sm"
					/>
				</Form.Group>

				{/* Email field (required) */}
				<Form.Group className="mb-3">
					<Form.Label className="small fw-semibold">Email *</Form.Label>
					<Form.Control
						type="email"
						placeholder="your@email.com"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						disabled={isLoading}
						size="sm"
					/>
				</Form.Group>

				{/* Phone field (optional) */}
				<Form.Group className="mb-3">
					<Form.Label className="small fw-semibold">Phone</Form.Label>
					<Form.Control
						type="tel"
						placeholder="(555) 123-4567"
						value={phone}
						onChange={(e) => setPhone(e.target.value)}
						disabled={isLoading}
						size="sm"
					/>
				</Form.Group>

				<Form.Group className="mb-3">
					<Form.Check
						type="checkbox"
						id="support-guest-accept-agreements"
						className="small"
						checked={isAccepted}
						onChange={(e) => setIsAccepted(e.target.checked)}
						disabled={isLoading}
						label={<>I accept the terms and conditions of these Agreements: <AgreementModalButton agreementKey="PRIVACY" agreementName="Privacy Policy" buttonText="Privacy Policy" />, <AgreementModalButton agreementKey="TERMS" agreementName="Terms of Use" buttonText="Terms of Use" />, <AgreementModalButton agreementKey="COMMUNICATION" agreementName="Communication Policy" buttonText="Communication Policy" />, and <AgreementsModalButton agreementKeys={['TELEHEALTH_CONSENT', 'READYRX_PRIVACY_PRACTICES']} buttonText="Consents" />.</>}
					/>
				</Form.Group>

				{/* Validation or server error alert */}
				{(validationError || error) && (
					<div className="alert alert-danger py-2 small mb-3">
						{validationError || error}
					</div>
				)}

				{/* Submit button with loading spinner */}
				<Button
					type="submit"
					variant="primary"
					className="w-100"
					disabled={!isAccepted || isLoading}
					size="sm"
				>
					{isLoading ? (
						<>
							<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
							Starting chat...
						</>
					) : (
						'Start Chat'
					)}
				</Button>
			</Form>
		</div>
	);
	//#endregion
};

export default GuestRegistrationForm;
